import { CreateLogger, notice, warn } from "@swivl-mono/helpers";
import { APIServiceConfigInterface, Log } from "@swivl-mono/types";

import { FMSServiceProviderInterface, FmsWriteGateInterface } from "../APIs/FMS.API";

const log = CreateLogger(Log.API, "service");

// ─── Retry-on-network-error helper ──────────────────────────────────────────
//
// Wraps `fetch` so the studio recovers when the server is briefly unreachable
// (typical case: dev-server restart). Backoff: ~250ms → 8s, capped at ~16s
// total. Application errors (400/401/404, app-thrown 5xx) are NOT retried —
// only true "server isn't reachable" signals.
//
// What counts as retryable:
//   - `TypeError: Failed to fetch` / NetworkError — the request never reached
//     the server, so retrying is always safe.
//   - HTTP 503 — a well-defined "service unavailable; try again", but retried
//     at most ONCE: a 503 often means the target is saturated (and on
//     server-to-self calls the target is the caller itself), so a full backoff
//     ladder multiplies inbound load ~7× and sustains the outage (ENG-2789).
//   - HTTP 502 / 504 — only on idempotent methods (GET / HEAD). Could mean
//     the server received the request but didn't finish, so retrying a write
//     could cause duplicates.

const RETRY_DELAYS_MS = [250, 500, 1000, 2000, 4000, 8000];

// See the 503 bullet above — one retry, then surface the error.
const MAX_503_RETRIES = 1;
// Per-attempt timeout. Without it a hung downstream service holds the caller
// (and anything the caller has open — DB transactions, advisory locks)
// indefinitely. 60s is a generous upper bound — far above any legitimate
// service-to-service call, including background workers with no router
// deadline — while still converting infinite hangs into bounded failures.
// Timeouts are NOT retried — a service that takes this long is down or
// wedged; fail fast.
const REQUEST_TIMEOUT_MS = 60000;

function isNetworkError(e: unknown): boolean {
  if (e instanceof TypeError) return true;
  const msg = (e as Error)?.message?.toLowerCase() ?? "";
  return msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("load failed");
}

async function sleep(ms: number): Promise<void> {
  await new Promise((r) => setTimeout(r, ms));
}

async function fetchWithRetry(url: string, init: RequestInit): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const idempotent = method === "GET" || method === "HEAD";
  let lastErr: unknown;

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      // Optional call: older browsers running the studio lack AbortSignal.timeout —
      // they silently keep the previous no-timeout behavior instead of throwing.
      const response = await fetch(url, { ...init, signal: AbortSignal.timeout?.(REQUEST_TIMEOUT_MS) });
      if (response.status === 503 || (idempotent && (response.status === 502 || response.status === 504))) {
        const maxRetries = response.status === 503 ? MAX_503_RETRIES : RETRY_DELAYS_MS.length;
        if (attempt < maxRetries) {
          await sleep(RETRY_DELAYS_MS[attempt]);
          continue;
        }
      }
      return response;
    } catch (e) {
      lastErr = e;
      if (attempt < RETRY_DELAYS_MS.length && isNetworkError(e)) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      throw e;
    }
  }
  throw lastErr ?? new Error("fetchWithRetry exhausted retries");
}

export interface APIServiceConfig {
  API?: APIServiceConfigInterface;
  FMS?: APIServiceConfigInterface;
  Sync?: APIServiceConfigInterface;
  Messaging?: APIServiceConfigInterface;
  LegacyServer?: APIServiceConfigInterface;
  Bull?: APIServiceConfigInterface;
  Analytics?: APIServiceConfigInterface;
}

export class APIService {
  serverURL: string;
  tokenType: string;
  context: string;
  static Server: APIService;
  static FMS: APIService;
  static Sync: APIService;
  static Messaging: APIService;
  static Channels: APIService;
  static Notifications: APIService;
  static LegacyServer: APIService;
  static Bull: APIService;
  static Bot: APIService;
  static Analytics: APIService;
  static FMSServiceProvider: FMSServiceProviderInterface;
  /** The Outbound Gate's FMS writer (ENG-3373); unset = FMS writes through the api-helper refuse to run. */
  static FmsWrite: FmsWriteGateInterface | undefined;

  authCallBack: () => string = () => "unset";
  errorCallback: <T>(e: Error, method?: string, url?: string) => Promise<T> = (e) => {
    throw new Error(e.message);
  };

  static log() {
    ["Server", "FMS", "Sync", "Messaging", "Channels", "LegacyServer", "Bull"].forEach((service: any) => {
      if ((APIService as any)[service]) {
        warn(service, APIService as any);
      } else {
        warn(service, "Not Set");
      }
    });
  }

  static init(config: {
    Server?: APIServiceConfigInterface;
    FMS?: APIServiceConfigInterface;
    Sync?: APIServiceConfigInterface;
    Messaging?: APIServiceConfigInterface;
    Channels?: APIServiceConfigInterface;
    Notifications?: APIServiceConfigInterface;

    LegacyServer?: APIServiceConfigInterface;
    Bull?: APIServiceConfigInterface;
    Bot?: APIServiceConfigInterface;
    Analytics?: APIServiceConfigInterface;
    FMSServiceProvider?: FMSServiceProviderInterface;
    FmsWrite?: FmsWriteGateInterface;
  }) {
    let APIServicesEnabled = "API Services Enabled:";
    if (config.Server) {
      APIServicesEnabled += " Server";
      APIService.Server = new APIService(config.Server);
    }
    if (config.FMS) {
      APIServicesEnabled += " FMS";
      APIService.FMS = new APIService(config.FMS);
    }
    if (config.Sync) {
      APIServicesEnabled += " Sync";
      APIService.Sync = new APIService(config.Sync);
    }
    if (config.Messaging) {
      APIServicesEnabled += " Messaging";
      APIService.Messaging = new APIService(config.Messaging);
    }
    if (config.Channels) {
      APIServicesEnabled += " Channels";
      APIService.Channels = new APIService(config.Channels);
    }
    if (config.LegacyServer) {
      APIServicesEnabled += " LegacyServer";
      APIService.LegacyServer = new APIService(config.LegacyServer);
    }
    if (config.Bull) {
      APIServicesEnabled += " Bull";
      APIService.Bull = new APIService(config.Bull);
    }
    if (config.Bot) {
      APIServicesEnabled += " Bot";
      APIService.Bot = new APIService(config.Bot);
    }
    if (config.Analytics) {
      APIServicesEnabled += " Analytics";
      APIService.Analytics = new APIService(config.Analytics);
    }
    if (config.FMSServiceProvider) {
      APIServicesEnabled += " FMSServiceProvider";
      APIService.FMSServiceProvider = config.FMSServiceProvider;
    }
    if (config.FmsWrite) {
      APIServicesEnabled += " FmsWrite";
      APIService.FmsWrite = config.FmsWrite;
    }
    if (config.Notifications) {
      APIServicesEnabled += " Notifications";
      APIService.Notifications = new APIService(config.Notifications);
    }
    notice(APIServicesEnabled);
  }

  constructor(config: APIServiceConfigInterface) {
    this.serverURL = config.url;
    this.authCallBack = config.authCallBack;
    this.errorCallback = config.errorCallback;
    this.tokenType = config.tokenType || "api";
    this.context = config.context || "unknown";
  }

  private buildQueryString(params?: any): string {
    if (!params) return "";
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      // Skip null/undefined — URLSearchParams.append stringifies them as
      // literal "null"/"undefined", which the TSOA route validator then
      // rejects (e.g. groupId: undefined → "?groupId=undefined" → "Validation
      // Failed: groupId is not a number"). Optional query params that
      // weren't supplied should simply be absent from the URL.
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item === undefined || item === null) continue;
          searchParams.append(key, String(item));
        }
      } else {
        searchParams.append(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return qs ? `?${qs}` : "";
  }

  private async sendWithBody<T>(method: "POST" | "PUT" | "PATCH", endpoint: string, body?: any) {
    const payload: any = {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json, application/xml, text/xml",
        "x-access-token": this.getAuth(),
        "x-token-type": this.tokenType
      }
    };
    // I want to see if the endpoint starts with "/../" then to return a url without the api part first
    const url = `${this.serverURL}/api${endpoint}`;

    if (body) {
      try {
        payload.body = JSON.stringify(body);
      } catch (e) {
        log("Error in stringify", e);
        return this.errorCatch<T>(e as Error, url, method);
      }
    }

    try {
      const response = await fetchWithRetry(url, payload);

      if (!response.ok) {
        const errorText = await response.text();
        // The server's own message is the useful one ("Conversation is already
        // escalated to another agent"). This used to `throw` it from INSIDE the
        // try, so the adjacent catch swallowed it and re-threw the generic
        // wrapper every time — callers could never reach the real reason
        // (ENG-3020 QA). Parse first, throw once, afterwards. The GET paths
        // below already surface `error.message` directly; this brings writes
        // in line with reads.
        let serverMessage: string | undefined;
        try {
          const errorJson = JSON.parse(errorText);
          if (typeof errorJson?.message === "string" && errorJson.message) serverMessage = errorJson.message;
        } catch {
          // Not JSON (an HTML error page, a proxy timeout) — keep the wrapper,
          // which at least carries the status and the raw body.
        }
        const error = new Error(serverMessage ?? `HTTP error ${response.status}: ${errorText}`);
        // Callers already probe for this (LocationVars' 403 permission check)
        // but nothing ever set it, so that branch was dead. Cast because Error
        // has no index signature.
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }

      if (response.status === 204) {
        return null as T;
      }

      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("xml")) {
        // Handle as XML
        const data = await response.text();
        return data as T;
      } else {
        // Handle as JSON
        const data = await response.json();
        return data as T;
      }
    } catch (e) {
      console.warn("Error in sendWithBody", body, url, method);
      return this.errorCatch<T>(e as Error, url, method);
    }
  }

  // TODO: headers param: temporary until we get fms service moved over to mono
  async get<T>(endpoint: string, params?: any, headers?: any): Promise<T> {
    const queryString = this.buildQueryString(params);

    const url = `${this.serverURL}/api${endpoint}${queryString}`;

    const defaultHeaders: { [key: string]: string } = {
      "Content-Type": "application/json",
      "x-access-token": this.getAuth(),
      "x-token-type": this.tokenType
    };

    // TODO: temporary until we get fms service moved over to mono
    const combinedHeaders = { ...defaultHeaders, ...headers };

    try {
      const response = await fetchWithRetry(url, {
        method: "GET",
        headers: combinedHeaders
      });

      //NOTE - 204 is a no content response (null)
      if (response.status === 204) {
        return null as T;
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      if (response.status === 204) {
        return null as T;
      }

      const data = await response.json();
      return data as T;
    } catch (e) {
      return this.errorCatch<T>(e as Error, url, "GET");
    }
  }

  async delete<T>(endpoint: string, params?: any, body?: any): Promise<T> {
    const queryString = this.buildQueryString(params);

    const url = `${this.serverURL}/api${endpoint}${queryString}`;

    try {
      const response = await fetchWithRetry(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": this.getAuth(),
          "x-token-type": this.tokenType
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      if (response.status === 204) {
        return null as T;
      }

      const data = await response.json();
      return data as T;
    } catch (e) {
      return this.errorCatch<T>(e as Error, url, "DELETE");
    }
  }

  async put<T>(endpoint: string, body?: any) {
    return this.sendWithBody<T>("PUT", endpoint, body);
  }

  async patch<T>(endpoint: string, body?: any) {
    return this.sendWithBody<T>("PATCH", endpoint, body);
  }

  async post<T>(endpoint: string, body?: any, params?: any) {
    const queryString = this.buildQueryString(params);
    endpoint += queryString;
    return this.sendWithBody<T>("POST", endpoint, body);
  }

  errorCatch<T>(error: Error, url: string, method: string): Promise<T> {
    if (this.errorCallback) {
      return this.errorCallback<T>(error, method, url);
    }

    throw new Error(error.message);
  }

  getAuth() {
    if (!this.authCallBack) {
      throw new Error("No Auth Callback Set");
    }
    const auth = this.authCallBack();

    if (auth === "unset") {
      throw new Error("No Auth Callback Set mk2");
    }
    return auth;
  }
}
